import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Zap, Heart, Hash } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, startOfMonth, endOfDay, subWeeks } from 'date-fns';

type Timeframe = 'this_week' | 'last_week' | 'month' | 'quarter' | 'alltime';

interface LeaderboardUser {
  user_id: string;
  full_name: string;
  prompts?: number;
  total_points?: number;
  engagement?: number;
}

interface LeaderboardBlockProps {
  orgId: string;
  onTitleClick?: () => void;
}

function getTimeframeRange(tf: Timeframe): { start: string | null; end: string | null } {
  const now = new Date();
  switch (tf) {
    case 'this_week': {
      const monday = startOfWeek(now, { weekStartsOn: 1 });
      return { start: monday.toISOString(), end: null };
    }
    case 'last_week': {
      const thisMonday = startOfWeek(now, { weekStartsOn: 1 });
      const lastMonday = subWeeks(thisMonday, 1);
      const lastSunday = endOfDay(new Date(thisMonday.getTime() - 1));
      return { start: lastMonday.toISOString(), end: lastSunday.toISOString() };
    }
    case 'month': {
      const monthStart = startOfMonth(now);
      return { start: monthStart.toISOString(), end: null };
    }
    case 'quarter': {
      const q = Math.floor(now.getMonth() / 3);
      const quarterStart = new Date(now.getFullYear(), q * 3, 1);
      return { start: quarterStart.toISOString(), end: null };
    }
    case 'alltime':
      return { start: null, end: null };
  }
}

function LeaderboardList({
  users,
  valueKey,
  suffix,
}: {
  users: LeaderboardUser[];
  valueKey: 'prompts' | 'total_points' | 'engagement';
  suffix: string;
}) {
  if (users.length === 0) {
    return (
      <div className="text-center py-6 text-muted-foreground text-sm">
        No data available
      </div>
    );
  }

  return (
    <div className="space-y-1 max-h-[200px] overflow-y-auto">
      {users.slice(0, 10).map((user, idx) => (
        <div key={user.user_id} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/30">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-muted-foreground text-sm w-5">{idx + 1}.</span>
            <span className="font-medium truncate">{user.full_name || 'Anonymous'}</span>
          </div>
          <span className="text-sm font-medium text-primary flex-shrink-0">
            {user[valueKey] || 0}{suffix}
          </span>
        </div>
      ))}
    </div>
  );
}

const TIMEFRAME_LABELS: Record<Timeframe, string> = {
  this_week: 'This Week',
  last_week: 'Last Week',
  month: 'Month',
  quarter: 'Quarter',
  alltime: 'All Time',
};

export function LeaderboardBlock({ orgId, onTitleClick }: LeaderboardBlockProps) {
  const [pointsLeaderboard, setPointsLeaderboard] = useState<LeaderboardUser[]>([]);
  const [engagementLeaderboard, setEngagementLeaderboard] = useState<LeaderboardUser[]>([]);
  const [activeLeaderboard, setActiveLeaderboard] = useState<LeaderboardUser[]>([]);
  const [timeframe, setTimeframe] = useState<Timeframe>('this_week');
  const [loading, setLoading] = useState(true);

  const fetchLeaderboards = async () => {
    const { start, end } = getTimeframeRange(timeframe);
    try {
      const [pointsRes, engagementRes, activeRes] = await Promise.all([
        supabase.rpc('get_org_points_leaderboard', { _org_id: orgId, _start_date: start, _end_date: end, _limit: 10 } as any),
        supabase.rpc('get_org_engagement_leaderboard', { _org_id: orgId, _start_date: start, _end_date: end, _limit: 10 } as any),
        supabase.rpc('get_org_active_leaderboard', { _org_id: orgId, _start_date: start, _end_date: end, _limit: 10 } as any),
      ]);

      if (pointsRes.data) setPointsLeaderboard(pointsRes.data as LeaderboardUser[]);
      if (engagementRes.data) setEngagementLeaderboard(engagementRes.data as LeaderboardUser[]);
      if (activeRes.data) setActiveLeaderboard(activeRes.data as LeaderboardUser[]);
    } catch (error) {
      console.error('Error fetching leaderboards:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    fetchLeaderboards();
  }, [orgId, timeframe]);

  if (loading) {
    return (
      <Card className="border-border overflow-hidden min-w-0">
        <CardHeader className="pb-2">
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-48 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border overflow-hidden min-w-0">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle
            className={`flex items-center gap-2 ${onTitleClick ? 'cursor-pointer hover:text-primary transition-colors' : ''}`}
            onClick={onTitleClick}
          >
            <Trophy className="h-5 w-5 text-yellow-500" />
            Leaderboards
          </CardTitle>
          <Select
            value={timeframe}
            onValueChange={(value: Timeframe) => setTimeframe(value)}
          >
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(TIMEFRAME_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="prompts" className="w-full">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="prompts" className="text-xs">
              <Hash className="h-3 w-3 mr-1" />
              Prompts
            </TabsTrigger>
            <TabsTrigger value="points" className="text-xs">
              <Zap className="h-3 w-3 mr-1" />
              Points
            </TabsTrigger>
            <TabsTrigger value="engagement" className="text-xs">
              <Heart className="h-3 w-3 mr-1" />
              Engagement
            </TabsTrigger>
          </TabsList>

          <TabsContent value="prompts" className="mt-0">
            <LeaderboardList users={activeLeaderboard} valueKey="prompts" suffix="" />
          </TabsContent>
          <TabsContent value="points" className="mt-0">
            <LeaderboardList users={pointsLeaderboard} valueKey="total_points" suffix=" pts" />
          </TabsContent>
          <TabsContent value="engagement" className="mt-0">
            <LeaderboardList users={engagementLeaderboard} valueKey="engagement" suffix="" />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}